import type { CSSProperties } from "react";

export interface SkeletonRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface SkeletonBlock extends SkeletonRect {
    /** CSS `border-radius` for the block. */
    borderRadius: string;
    /** Largest corner radius in px, clamped to half the shortest side. */
    cornerRadius: number;
}

/** Visible area as edges in the same coordinates as `SkeletonRect`; infinite edges don't clip. */
export interface SkeletonClip {
    left: number;
    top: number;
    right: number;
    bottom: number;
}

export const UNCLIPPED: SkeletonClip = {
    left: Number.NEGATIVE_INFINITY,
    top: Number.NEGATIVE_INFINITY,
    right: Number.POSITIVE_INFINITY,
    bottom: Number.POSITIVE_INFINITY,
};

const MIN_RECT_SIZE = 0.5;
const MIN_OUTLINE_EDGE_PX = 2;
const OUTLINE_MASK_LAYER = "linear-gradient(#000 0 0)";

/**
 * Resolves a computed `border-radius`; implementation inspired by phantom-ui's
 * `normalizeRadius` (https://github.com/Aejkatappaja/phantom-ui, MIT): an unset radius
 * (`0px`) falls back to `fallbackRadius`. Also returns the largest corner in px
 * so the outline mask can cover the rounded corners.
 */
export function resolveBlockRadius(
    computedRadius: string,
    rect: SkeletonRect,
    fallbackRadius: number,
): Pick<SkeletonBlock, "borderRadius" | "cornerRadius"> {
    const shortestSide = Math.min(rect.width, rect.height);
    const borderRadius =
        computedRadius === "0px" ? `${fallbackRadius}px` : computedRadius;
    const largestCorner = Math.max(
        ...borderRadius.split(/[\s/]+/).map((value) => {
            const amount = Number.parseFloat(value);

            return value.endsWith("%") ? (amount / 100) * shortestSide : amount;
        }),
    );

    return {
        borderRadius,
        cornerRadius: Math.min(largestCorner, shortestSide / 2),
    };
}

/**
 * Collapses inline-box fragments (text nodes, nested inline elements) into one
 * rect per rendered line, so a wrapped paragraph becomes one bar per line.
 */
export function mergeLineRects(rects: readonly SkeletonRect[]): SkeletonRect[] {
    const sortedRects = rects
        .filter((rect) => rect.width > MIN_RECT_SIZE && rect.height > MIN_RECT_SIZE)
        .toSorted((a, b) => a.y - b.y || a.x - b.x);
    const lines: SkeletonRect[] = [];

    for (const rect of sortedRects) {
        const line = lines.at(-1);

        if (line && isSameLine(line, rect)) {
            const right = Math.max(line.x + line.width, rect.x + rect.width);
            const bottom = Math.max(line.y + line.height, rect.y + rect.height);
            line.x = Math.min(line.x, rect.x);
            line.y = Math.min(line.y, rect.y);
            line.width = right - line.x;
            line.height = bottom - line.y;
            continue;
        }

        lines.push({ ...rect });
    }

    return lines;
}

export function intersectClips(a: SkeletonClip, b: SkeletonClip): SkeletonClip {
    return {
        left: Math.max(a.left, b.left),
        top: Math.max(a.top, b.top),
        right: Math.min(a.right, b.right),
        bottom: Math.min(a.bottom, b.bottom),
    };
}

/** The part of `rect` inside `clip`, or `null` when nothing visible remains. */
export function clipRect(rect: SkeletonRect, clip: SkeletonClip): SkeletonRect | null {
    const left = Math.max(rect.x, clip.left);
    const top = Math.max(rect.y, clip.top);
    const right = Math.min(rect.x + rect.width, clip.right);
    const bottom = Math.min(rect.y + rect.height, clip.bottom);

    if (right - left <= MIN_RECT_SIZE || bottom - top <= MIN_RECT_SIZE) {
        return null;
    }

    return { x: left, y: top, width: right - left, height: bottom - top };
}

/** Orders blocks top-to-bottom, then left-to-right, which drives the stagger. */
export function sortBlocksByReadingOrder<T extends SkeletonRect>(
    blocks: readonly T[],
): T[] {
    return blocks.toSorted((a, b) => Math.round(a.y - b.y) || a.x - b.x);
}

/**
 * Per-block delay (`stepMs`) and the delay of the last block (`spreadMs`),
 * keeping the whole cascade within `maxSpreadMs`.
 */
export function getStagger(
    blockCount: number,
    stepMs: number,
    maxSpreadMs: number,
): { stepMs: number; spreadMs: number } {
    if (blockCount < 2) {
        return { stepMs: 0, spreadMs: 0 };
    }

    const step = Math.min(stepMs, maxSpreadMs / (blockCount - 1));

    return { stepMs: step, spreadMs: step * (blockCount - 1) };
}

/**
 * Reveal delay for real content at `rect`: the draw delay of the nearest
 * (placeholder) block, so content resolves in the same order and rhythm the
 * outlines drew in, even where the real layout differs from the placeholder.
 */
export function getRevealDelayMs(
    rect: SkeletonRect,
    blocks: readonly SkeletonRect[],
    stepMs: number,
): number {
    const centerX = rect.x + rect.width / 2;
    const centerY = rect.y + rect.height / 2;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    blocks.forEach((block, index) => {
        // Vertical distance dominates: the stagger runs in reading order, top to bottom.
        const distance =
            Math.abs(block.y + block.height / 2 - centerY) * 4 +
            Math.abs(block.x + block.width / 2 - centerX);

        if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
        }
    });

    return nearestIndex * stepMs;
}

/**
 * Mask that reveals the block outline as one continuous clockwise stroke driven
 * by `--skeleton-draw` (0 → 1): it leaves the top-left corner along the top
 * edge, runs down the right, back along the bottom, and closes up the left edge
 * into the top-left corner again.
 *
 * Each edge is one solid mask layer `edge` px thick (enough to cover its rounded
 * corner) that grows once the stroke has travelled past the previous edges.
 * The left strip spans the full height, so it owns both left corners and the
 * stroke closes exactly where it started.
 */
export function getOutlineMaskStyle(block: SkeletonBlock): CSSProperties {
    const edge = Math.min(
        Math.max(block.cornerRadius, MIN_OUTLINE_EDGE_PX),
        Math.min(block.width, block.height) / 2,
    );
    const horizontal = block.width - edge;
    const vertical = block.height;
    const perimeter = 2 * horizontal + 2 * vertical;
    const travelled = (offset: number, length: number) =>
        `clamp(0px, calc(var(--skeleton-draw) * ${px(perimeter)} - ${px(offset)}), ${px(length)})`;
    const layers = [
        { position: `${px(edge)} 0`, size: `${travelled(0, horizontal)} ${px(edge)}` },
        { position: "100% 0", size: `${px(edge)} ${travelled(horizontal, vertical)}` },
        {
            position: "100% 100%",
            size: `${travelled(horizontal + vertical, horizontal)} ${px(edge)}`,
        },
        {
            position: "0 100%",
            size: `${px(edge)} ${travelled(2 * horizontal + vertical, vertical)}`,
        },
    ];

    return {
        maskImage: layers.map(() => OUTLINE_MASK_LAYER).join(", "),
        maskRepeat: "no-repeat",
        maskPosition: layers.map((layer) => layer.position).join(", "),
        maskSize: layers.map((layer) => layer.size).join(", "),
    };
}

export function isSameBlocks(
    a: readonly SkeletonBlock[] | null,
    b: readonly SkeletonBlock[],
): boolean {
    if (!a || a.length !== b.length) {
        return false;
    }

    return a.every((block, index) => {
        const other = b[index];

        return (
            block.x === other.x &&
            block.y === other.y &&
            block.width === other.width &&
            block.height === other.height &&
            block.borderRadius === other.borderRadius
        );
    });
}

function isSameLine(line: SkeletonRect, rect: SkeletonRect): boolean {
    const overlap =
        Math.min(line.y + line.height, rect.y + rect.height) - Math.max(line.y, rect.y);

    return overlap > Math.min(line.height, rect.height) / 2;
}

function px(value: number): string {
    return `${Math.round(value * 100) / 100}px`;
}
